import React, { useState } from 'react'

import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
} from '@material-ui/core'

import { SortableFinder } from '../../../../../components/SortableFinder/SortableFinder'
import { Folder } from '../../../../../models'
import { defaultLocationSelection } from '../../../../../store'

interface Props {
    infrastructure: Folder
    saving: boolean
    onClose: () => void
    onSave: (unitIds: number[]) => void
}

const compareNames = (left: string, right: string, order: 'asc' | 'desc') => {
    const result = left.localeCompare(right, undefined, {
        numeric: true,
        sensitivity: 'base',
    })
    return order === 'asc' ? result : -result
}

export const unitsInFolderOrder = (
    root: Folder,
    sortState: { [folderId: number]: 'asc' | 'desc' },
): number[] => {
    const ids: number[] = []

    const visit = (folder: Folder) => {
        const order = sortState[folder.id] || 'asc'
        const children = [...folder.children].sort((left, right) =>
            compareNames(left.name, right.name, order),
        )
        children.forEach(visit)

        const units = [...folder.units].sort((left, right) =>
            compareNames(left.name, right.name, order),
        )
        units.forEach((unit) => ids.push(unit.id))
    }

    visit(root)
    return ids
}

export const WalkUnitOrderDialog = ({
    infrastructure,
    saving,
    onClose,
    onSave,
}: Props) => {
    const [sortState, setSortState] = useState<{
        [folderId: number]: 'asc' | 'desc'
    }>({})

    return (
        <Dialog
            open
            onClose={saving ? undefined : onClose}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle>Order Units</DialogTitle>
            <DialogContent
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '60vh',
                }}
            >
                <DialogContentText>
                    This is the order EZ Scheduler uses. Save it, then run EZ
                    Scheduler again.
                </DialogContentText>
                <div style={{ flex: 1, minHeight: 0 }}>
                    <SortableFinder
                        root={infrastructure}
                        locationSelection={defaultLocationSelection}
                        enableSorting
                        numericNameSort
                        showAllUnits
                        showSelectionIcon={false}
                        defaultOpenFirstLevel
                        defaultSortOrder="asc"
                        onSortChange={(folderId, order) => {
                            setSortState((current) => ({
                                ...current,
                                [folderId]: order,
                            }))
                        }}
                        maxHeight="100%"
                    />
                </div>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={saving}>
                    Cancel
                </Button>
                <Button
                    color="primary"
                    variant="contained"
                    disabled={saving}
                    onClick={() =>
                        onSave(unitsInFolderOrder(infrastructure, sortState))
                    }
                >
                    {saving ? 'Saving…' : 'Save Order'}
                </Button>
            </DialogActions>
        </Dialog>
    )
}
